import { Link } from 'react-router-dom';
import { Database } from 'lucide-react';

const LAST_UPDATED = 'May 17, 2026';

export default function Privacy() {
  return (
    <div className="mx-auto max-w-3xl space-y-8 p-6 md:p-10">
      <div className="space-y-3">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <div className="flex size-7 items-center justify-center rounded-md bg-gradient-to-br from-primary to-primary/80 text-primary-foreground">
            <Database className="size-4" />
          </div>
          <span className="font-medium">DataBro</span>
        </Link>
        <h1 className="text-3xl font-semibold tracking-tight">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground">Last updated: {LAST_UPDATED}</p>
      </div>

      <section className="prose prose-sm dark:prose-invert max-w-none space-y-4">
        <p>
          DataBro is an internal workflow tool used by authorized members of our
          team for capturing and replaying API requests during routine e-commerce
          data collection. The DataBro Chrome extension and the dashboard at
          databro.kudcrafts.com are distributed unlisted on the Chrome Web Store
          and are intended solely for internal team use. This page explains what
          data the extension and dashboard collect, why we collect it, and what
          we do with it.
        </p>

        <h2 className="text-xl font-semibold">Who this policy applies to</h2>
        <p>
          This policy applies only to authorized teammates we have explicitly
          granted access to. The extension is not intended for general public
          use and is distributed only via a private install link.
        </p>

        <h2 className="text-xl font-semibold">What we collect</h2>
        <p>
          When an authorized operator triggers a capture from the dashboard or
          from a tab they are signed in to, the extension records and transmits
          to our private backend at databro.kudcrafts.com:
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>
            The HTTP request the operator wishes to replay later, including its
            URL, method, headers, cookies, and request body.
          </li>
          <li>
            The corresponding HTTP response body returned by the third-party
            service at the time of capture.
          </li>
          <li>
            The operator's Google account email (read via the
            <code className="mx-1 rounded bg-muted px-1 py-0.5 text-xs">chrome.identity</code>
            API) so we can attribute the capture to the correct teammate in our
            internal audit log.
          </li>
          <li>
            A locally generated client identifier and the operator's API key for
            our backend, stored in
            <code className="mx-1 rounded bg-muted px-1 py-0.5 text-xs">chrome.storage</code>
            so jobs persist across browser restarts.
          </li>
          <li>
            Operational metadata about each capture and replay job (status,
            timestamps, error messages).
          </li>
        </ul>

        <h2 className="text-xl font-semibold">What we do NOT collect</h2>
        <ul className="ml-6 list-disc space-y-1">
          <li>We do not record general browsing history or pages the operator visits outside of an explicit capture action.</li>
          <li>We do not collect personal data of end users of third-party services other than what is incidentally present in an API request the operator chooses to capture.</li>
          <li>We do not collect keystrokes, screen contents, microphone, or camera input.</li>
          <li>We do not use analytics or advertising trackers.</li>
        </ul>

        <h2 className="text-xl font-semibold">How we use the data</h2>
        <p>
          Captured request and response data is used exclusively to replay the
          same request later in a controlled internal pipeline. Operator email
          and client identifier are used to authorize and audit access. We do
          not sell, share, rent, or otherwise transfer collected data to any
          third party.
        </p>

        <h2 className="text-xl font-semibold">Where the data is stored</h2>
        <p>
          All captured data and operational metadata is stored on our private
          backend infrastructure hosted under the kudcrafts.com domain. Local
          extension state is stored on the operator's own device via the Chrome
          extension storage API. We do not transmit data to any other third
          parties.
        </p>

        <h2 className="text-xl font-semibold">Permissions used by the extension</h2>
        <ul className="ml-6 list-disc space-y-1">
          <li><strong>host_permissions (&lt;all_urls&gt;), tabs, scripting:</strong> required to intercept and replay API requests on whichever e-commerce platform the operator is currently authenticated to. The extension only acts on tabs from which an operator explicitly initiates a capture.</li>
          <li><strong>storage:</strong> persists the operator's API key, local client identifier, and pending job queue.</li>
          <li><strong>alarms:</strong> schedules periodic replays at intervals set by the operator.</li>
          <li><strong>notifications:</strong> notifies the operator when a long-running job completes or fails.</li>
          <li><strong>identity:</strong> reads the operator's Google account email so the backend can attribute jobs to the correct teammate.</li>
        </ul>

        <h2 className="text-xl font-semibold">Data retention</h2>
        <p>
          Captured request and response data is retained on our backend for as
          long as it remains operationally useful for the team's workflow.
          Operators or administrators can request deletion of captured data at
          any time by contacting us at the address below.
        </p>

        <h2 className="text-xl font-semibold">Your choices</h2>
        <p>
          As an authorized operator, you can uninstall the extension at any time
          via <code className="rounded bg-muted px-1 py-0.5 text-xs">chrome://extensions</code>.
          Uninstalling removes all locally stored extension data on your device.
          To request deletion of data already transmitted to our backend, contact
          us at the address below.
        </p>

        <h2 className="text-xl font-semibold">Children's privacy</h2>
        <p>
          DataBro is an internal workplace tool and is not directed at children.
          We do not knowingly collect data from anyone under 13.
        </p>

        <h2 className="text-xl font-semibold">Changes to this policy</h2>
        <p>
          We may update this policy as the tool evolves. The "Last updated" date
          at the top of this page reflects the most recent revision.
        </p>

        <h2 className="text-xl font-semibold">Contact</h2>
        <p>
          For questions about this policy or to request deletion of your data,
          contact us at{' '}
          <a
            href="mailto:datacuan.hq@gmail.com"
            className="text-primary underline-offset-4 hover:underline"
          >
            datacuan.hq@gmail.com
          </a>
          .
        </p>
      </section>

      <div className="border-t pt-6 text-center text-xs text-muted-foreground">
        <Link to="/install" className="hover:text-foreground">
          Install DataBro
        </Link>
      </div>
    </div>
  );
}
